// Frontend/src/components/EmployeePortal.jsx
import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useManagerStatus } from "../hooks/manager_portal_hooks";
import {
  User,
  LogOut,
  Briefcase,
  Menu,
  X,
  WalletCards,
  CalendarDays,
  Users,
  BarChart3,
  FileText,
  Clock,
} from "lucide-react";

// Profile Components
import PersonalInformation from "./profile/PersonalInformation";

// New layout components
import EmployeeCompensation from "./employee/EmployeeCompensation";
import EmployeeLeaveManagement from "./employee/EmployeeLeaveManagement";
import EmployeeCalendar from "./employee/EmployeeCalendar";
import OrganizationalHierarchy from "./employee/OrganizationalHierarchy";
import EmployeeTimesheet from "./employee/EmployeeTimesheet";
import EmployeePayrollDocuments from "./employee/EmployeePayrollDocuments";

import ManagerPersonalOverview from "./manager/ManagerPersonalOverview";
import ManagerTimesheet from "./manager/ManagerTimesheet";
import ManagerCompensation from "./manager/ManagerCompensation";
import ManagerPersonalLeave from "./manager/ManagerPersonalLeave";
import ManagerCalendar from "./manager/ManagerCalendar";
import ManagerTeamMember from "./manager/ManagerTeamMember";
import ManagerTeamLeave from "./manager/ManagerTeamLeave";
import ManagerDashboard from "./manager/ManagerDashboard";
import ManagerPayrollDocuments from "./manager/ManagerPayrollDocuments";

// Common Components
import Footer from "./common/Footer";
import UserProfileHeader from "./common/UserProfileHeader";
import PageHeader from "./common/PageHeader";
import {
  PageHeaderProvider,
  usePageHeader,
} from "../contexts/PageHeaderContext";

// Assets
import { ReactComponent as DogmaGroupLogo } from "../assets/dogma-group-logo.svg";

const EmployeePortalContent = ({ onLogout }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { headerState } = usePageHeader();
  // Sidebar open by default on desktop, closed on mobile
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    return window.innerWidth >= 768; // md breakpoint
  });

  // Manager status is derived from the EssWorkerManagerRelations API:
  // the user is a manager when they have at least one direct report.
  const { isManager, isLoading: managerStatusLoading } = useManagerStatus();
  const portalTitle = isManager ? "Line Manager Portal" : "Employee Portal";

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  const employeeMenuItems = [
    {
      name: "Personal Information",
      path: "/portal/profile",
      icon: User,
    },
    {
      name: "Timesheet Details",
      path: "/portal/timesheet",
      icon: Clock,
    },
    // {
    //   name: "Organizational Hierarchy",
    //   path: "/portal/hierarchy",
    //   icon: Users,
    // },
    {
      name: "Payslip and Benefits",
      path: "/portal/compensation",
      icon: WalletCards,
    },
    {
      name: "Payroll Documents",
      path: "/portal/payroll-documents",
      icon: FileText,
    },
    {
      name: "My Calendar",
      path: "/portal/calendar",
      icon: CalendarDays,
    },
    {
      name: "My Leave Details",
      path: "/portal/leave/management",
      icon: Briefcase,
    },
  ];

  const managerMenuItems = [
    {
      name: "Manager Dashboard",
      path: "/portal/manager/dashboard",
      icon: BarChart3,
    },
    {
      name: "Personal Information",
      path: "/portal/manager/overview",
      icon: User,
    },
    {
      name: "Timesheet Details",
      path: "/portal/manager/timesheet",
      icon: Clock,
    },
    {
      name: "Payslip and Benefits",
      path: "/portal/manager/compensation",
      icon: WalletCards,
    },
    {
      name: "Payroll Documents",
      path: "/portal/manager/payroll-documents",
      icon: FileText,
    },
    {
      name: "My Calendar",
      path: "/portal/manager/calendar",
      icon: CalendarDays,
    },
    {
      name: "My Leave Details",
      path: "/portal/manager/personal-leave",
      icon: Briefcase,
    },
    {
      name: "Team Member",
      path: "/portal/manager/team",
      icon: Users,
    },
    {
      name: "Team Leave",
      path: "/portal/manager/team-leave",
      icon: CalendarDays,
    },
  ];

  const menuItems = isManager ? managerMenuItems : employeeMenuItems;

  const handleLogout = () => {
    onLogout();
    navigate("/login");
  };

  // Wait for the manager-status check before rendering the portal, so we don't
  // flash the wrong menu/routes then swap them once the API responds.
  if (managerStatusLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        <p className="mt-4 text-indigo-600 font-semibold">Loading your portal...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex relative">
      {/* Sidebar Overlay - Mobile only */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        } w-64 bg-white shadow-xl transition-all duration-300 overflow-hidden fixed md:fixed h-screen z-50 border-r border-gray-100`}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0 bg-gradient-to-r from-indigo-50 to-white min-h-[60px] sm:min-h-[72px]">
            <div className="flex items-center justify-between w-full">
              <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-indigo-700 bg-clip-text text-transparent">
                {portalTitle}
              </h1>
              <button
                onClick={() => setSidebarOpen(false)}
                className="md:hidden text-gray-500 hover:text-gray-700 focus:outline-none transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <a
              href="https://dogmagroup.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 mt-1 group"
            >
              <span className="text-xs text-gray-500 group-hover:text-indigo-600 transition-colors">
                Powered by
              </span>
              <DogmaGroupLogo className="h-3 w-auto text-gray-400 group-hover:text-indigo-600 transition-colors" />
            </a>
          </div>

          <nav className="flex-1 overflow-y-auto p-4 flex-shrink">
            <div className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                // Check if current path matches this menu item
                // Also consider /portal as matching the first menu item (Personal Information)
                const isActive =
                  location.pathname === item.path ||
                  (location.pathname === "/portal" &&
                    item.path === "/portal/profile") ||
                  (location.pathname === "/portal/" &&
                    item.path === "/portal/profile");
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => {
                      // Only close sidebar on mobile devices
                      if (window.innerWidth < 768) {
                        setSidebarOpen(false);
                      }
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      isActive
                        ? "bg-gradient-to-r from-indigo-50 to-indigo-100 text-indigo-700"
                        : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600"
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${isActive ? "text-indigo-600" : ""}`}
                    />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          <div className="p-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-white hover:text-indigo-600 rounded-xl transition-all duration-200 border border-transparent hover:border-gray-200 shadow-sm"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col w-full transition-all duration-300 ${sidebarOpen ? "md:ml-64" : "md:ml-0"}`}
      >
        {/* Mobile Header */}
        <header className="bg-white shadow-md md:hidden p-4 flex items-center justify-between flex-shrink-0 sticky top-0 z-30 border-b border-gray-100">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-500 hover:text-indigo-600 focus:outline-none transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-gray-800">{portalTitle}</h1>
          <a
            href="https://shpsandbox.crm11.dynamics.com/main.aspx"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-sm"
            title="SiriusConnect365"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </a>
        </header>

        {/* Top Header Section with Page Header and User Profile */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-4">
            {/* Page Header */}
            {headerState.title && (
              <div className="flex-1 min-w-0">
                <PageHeader
                  icon={headerState.icon}
                  title={headerState.title}
                  subtitle={headerState.subtitle}
                  compact={true}
                />
              </div>
            )}
            {/* SiriusConnect365 Link and User Profile Header */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* SiriusConnect365 Link */}
              <a
                href="https://shpsandbox.crm11.dynamics.com/main.aspx"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                SHP CRM
              </a>
              {/* User Profile Header */}
              <UserProfileHeader
                profilePath={
                  isManager ? "/portal/manager/overview" : "/portal/profile"
                }
              />
            </div>
          </div>
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto w-full bg-gray-50/50">
          <div className="min-h-full">
            <Routes>
              {/* Employee routes */}
              <Route path="profile/*" element={<PersonalInformation />} />
              <Route path="compensation" element={<EmployeeCompensation />} />
              <Route
                path="payroll-documents"
                element={<EmployeePayrollDocuments />}
              />
              <Route
                path="leave/management"
                element={<EmployeeLeaveManagement />}
              />
              <Route path="timesheet" element={<EmployeeTimesheet />} />
              <Route path="calendar" element={<EmployeeCalendar />} />
              {/* <Route path="hierarchy" element={<OrganizationalHierarchy />} /> */}

              {/* Manager-specific routes */}
              {isManager && (
                <>
                  <Route
                    path="manager/dashboard"
                    element={<ManagerDashboard />}
                  />
                  <Route
                    path="manager/overview"
                    element={<ManagerPersonalOverview />}
                  />
                  <Route
                    path="manager/timesheet"
                    element={<ManagerTimesheet />}
                  />
                  <Route
                    path="manager/compensation"
                    element={<ManagerCompensation />}
                  />
                  <Route
                    path="manager/payroll-documents"
                    element={<ManagerPayrollDocuments />}
                  />
                  <Route
                    path="manager/personal-leave"
                    element={<ManagerPersonalLeave />}
                  />
                  <Route
                    path="manager/calendar"
                    element={<ManagerCalendar />}
                  />
                  <Route path="manager/team" element={<ManagerTeamMember />} />
                  <Route
                    path="manager/team-leave"
                    element={<ManagerTeamLeave />}
                  />
                </>
              )}

              {/* Default landing routes */}
              <Route
                index
                element={
                  isManager ? <ManagerDashboard /> : <PersonalInformation />
                }
              />
            </Routes>
          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
};

const EmployeePortal = ({ onLogout }) => {
  return (
    <PageHeaderProvider>
      <EmployeePortalContent onLogout={onLogout} />
    </PageHeaderProvider>
  );
};

export default EmployeePortal;
